import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { isFetchBaseQueryError } from "@utils/api.util";
import deepEqual from "deep-equal";
import { MutableRefObject, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Blocker, useBlocker } from "react-router";
import { showToast } from "../store/statusSlice";
import type { AppDispatch, RootState } from "../store/store";

type FetchBaseQueryErrorWithData = FetchBaseQueryError & {
  data: { message: string[] | string };
};

type ErrorType =
  | string
  | FetchBaseQueryError
  | FetchBaseQueryErrorWithData
  | SerializedError;

type ToastEffect = {
  isError: boolean;
  error?: ErrorType;
  isSuccess?: boolean;
  successMessage?: string;
};

export const useToastEffect = (toast: ToastEffect) => {
  const { t } = useTranslation(["toast"]);
  const dispatch = useDispatch();

  const errorMes = getErrorMessage(toast.error);

  useEffect(() => {
    if (toast.isSuccess) {
      dispatch(
        showToast({
          message: toast.successMessage || t("hooks.successSave"),
          type: "success",
        }),
      );
    } else if (toast.isError) {
      dispatch(
        showToast({
          message: errorMes,
          type: "error",
        }),
      );
    }
  }, [
    toast.isSuccess,
    toast.isError,
    dispatch,
    errorMes,
    toast.successMessage,
    t,
  ]);
};

const getErrorMessage = (error: ErrorType | undefined): string => {
  const { t } = useTranslation(["toast"]);
  if (!error) {
    return t("hooks.error");
  }

  if (isString(error)) {
    return error;
  }
  if (isSerializedError(error) && error?.message) {
    return error.message;
  }
  if (
    isFetchBaseQueryError(error) &&
    typeof error.status === "number" &&
    typeof error.data === "string"
  ) {
    return error.data;
  }
  if (isFetchBaseQueryErrorWithData(error)) {
    if (Array.isArray(error.data?.message)) {
      return error.data?.message?.join(", ");
    }
    return error.data?.message;
  }

  return t("error");
};

const isString = (error: ErrorType): error is string => {
  return typeof error === "string";
};

const isSerializedError = (error: ErrorType): error is SerializedError => {
  return (
    (error as FetchBaseQueryError).status === undefined &&
    (error as SerializedError)?.message !== undefined
  );
};

const isFetchBaseQueryErrorWithData = (
  error: ErrorType,
): error is FetchBaseQueryErrorWithData => {
  const castedError = error as FetchBaseQueryErrorWithData;
  return (
    isFetchBaseQueryError(error) &&
    castedError.data?.message !== undefined &&
    (typeof castedError?.data.message === "string" ||
      Array.isArray(castedError.data?.message))
  );
};

export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();

export function useOnChangeBlocker<T>(
  initialRef: MutableRefObject<T>,
  data: T,
  disableBlocking = false,
): Blocker {
  const when = !disableBlocking && !deepEqual(initialRef.current, data);
  return useNavigationBlocker(when);
}

/**
 * Prompt users when navigating away or closing browser with unsaved changes.
 * @param when - condition to activate prompt
 */
export function useNavigationBlocker(when: boolean, message?: string): Blocker {
  const blocker = useBlocker(when);
  const { t } = useTranslation(["commons"]);

  useEffect(() => {
    if (blocker.state === "blocked") {
      const userPrompt = message ?? t("warningUnsavedChanges");
      const proceed = window.confirm(userPrompt);
      if (proceed) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker, t, message]);

  useEffect(() => {
    if (!when) {
      return;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = true;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [when]);

  useEffect(() => {
    if (!when && blocker.state === "blocked") {
      blocker.reset();
    }
  }, [when, blocker]);

  return blocker;
}
